import { ConfirmationModal } from "@/molecules";
import { CountriesDropdown } from "@/molecules/dropdownMenus/CountriesDropdown/CountriesDropdown";
import {
  IconArrowBackUp,
  IconArrowRight,
  IconClock,
  IconListDetails,
  IconVersions,
} from "@tabler/icons-react";
import { cloneDeep, isEmpty } from "lodash-es";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useOutletContext } from "react-router-dom";

import { OneleetRole, TenantRole, Vendor } from "@oneleet/api_client";
import {
  CurrentMembershipContextType,
  DEFAULT_VENDOR_RISK_ASSESSMENT,
  Iso3166Alpha2Code,
  OutletContext,
  RiskAssessmentFormV1,
  RiskAssessmentItemV1,
  UserContextType,
  cn,
  conditionPasses,
  getSelectedItems,
  getSelectedOrUndefinedItems,
  itemHasValue,
  riskAssessmentCategories,
  riskAssessmentQuestionnaireVersion,
  setRiskAssessmentFormValue,
  useNotify,
} from "@oneleet/utils";

import { AnimatePresence, motion } from "framer-motion";
import { Info } from "lucide-react";

import {
  NgButton,
  NgCheckbox,
  NgIconButton,
  NgTextArea,
  NgTooltip,
  Radio,
  Tooltip,
} from "../../atoms";

/**
 * Vendor risk assessment questionnaire.
 *
 * The form is only editable when a `setFormData` callback is provided.
 *
 * Now exposes activeCategory and scrollable container ref via props.
 */
export type VendorRiskAssessmentHandle = {
  activeCategory: string | null;
  scrollToCategory: (categoryTitle: string) => void;
};

export const VendorRiskAssessment = forwardRef<
  VendorRiskAssessmentHandle,
  {
    vendor: Vendor;

    formData: RiskAssessmentFormV1;
    setFormData?: React.Dispatch<React.SetStateAction<RiskAssessmentFormV1>>;

    forTemplate?: boolean;
    templateData?: RiskAssessmentFormV1;

    scrollContainerRef?: React.RefObject<HTMLElement | null>;
    onActiveCategoryChange?: (category: string | null) => void;
  }
>(
  (
    {
      vendor,

      formData,
      setFormData,

      forTemplate = false,
      templateData,

      scrollContainerRef,
      onActiveCategoryChange,
    },
    ref,
  ) => {
    const { currentMembership, user: currentUser } = useOutletContext<
      CurrentMembershipContextType & UserContextType & OutletContext
    >();

    const canEdit =
      setFormData &&
      (currentMembership?.role === TenantRole.ADMIN ||
        currentUser?.oneleetRole === OneleetRole.SUPERADMIN);

    const setFormValue = useCallback(
      (itemId: string, value?: string | string[]) => {
        if (!formData) return;
        setFormData?.((prev) =>
          setRiskAssessmentFormValue(prev, itemId, value),
        );
      },
      [formData, setFormData],
    );

    const questionnaireVersion = riskAssessmentQuestionnaireVersion(formData);
    const canUpgradeQuestionnaireVersion =
      canEdit &&
      questionnaireVersion !==
        DEFAULT_VENDOR_RISK_ASSESSMENT.questionnaireVersion;

    const [isUpgradeConfirmationModalOpen, setIsUpgradeConfirmationModalOpen] =
      useState(false);
    const upgradePostmortemNotify = useNotify();

    const upgradeQuestionnaireVersion = () => {
      if (!canEdit) return;
      setIsUpgradeConfirmationModalOpen(false);

      setFormData?.((prevForm) => {
        const template = vendor.assessmentTemplate as
          | RiskAssessmentFormV1
          | undefined;

        let newForm = cloneDeep(
          template?.questionnaireVersion ===
            DEFAULT_VENDOR_RISK_ASSESSMENT.questionnaireVersion
            ? /* Template is up-to-date, so use it */
              template
            : /* Template is out-of-date or does not exist;
            since we are explicitly trying to upgrade the questionnaire, fall back on an empty form */
              DEFAULT_VENDOR_RISK_ASSESSMENT,
        );

        let countMigrated = 0;
        const prevValues = getSelectedItems(prevForm);
        const migrateValue = (oldItemId: string, newItemId: string) => {
          newForm = setRiskAssessmentFormValue(
            newForm,
            newItemId,
            prevValues[oldItemId],
          );
          countMigrated += 1;
        };

        // Migrate answers between questionnaire versions when possible
        switch (prevForm.questionnaireVersion) {
          case "Legacy":
            switch (newForm.questionnaireVersion) {
              case "2025-04-28":
                migrateValue("data_encryption", "encrypt_at_rest");
                break;
            }
            break;
        }

        const newValues = getSelectedOrUndefinedItems(newForm);
        const countRemaining = Object.keys(newValues).length - countMigrated;

        upgradePostmortemNotify({
          title: "Migration complete",
          type: "success",
          description: countMigrated
            ? `Migrated ${countMigrated} answer${countMigrated === 1 ? "" : "s"} from the previous questionnaire. Please answer the remaining ${countRemaining} new question${countRemaining === 1 ? "" : "s"}.`
            : `Please answer ${countRemaining} new question${countRemaining === 1 ? "" : "s"}.`,
        });

        return newForm;
      });
    };

    const selectedItems = useMemo(() => {
      return getSelectedItems(formData);
    }, [formData]);
    const selectedTemplateItems = useMemo(() => {
      return templateData ? getSelectedItems(templateData) : {};
    }, [templateData]);

    // Get all items that are applicable
    const allApplicableItems = useMemo(() => {
      return riskAssessmentCategories(formData).flatMap((category) =>
        !category.applicableWhen ||
        conditionPasses(formData, selectedItems, category.applicableWhen)
          ? category.items.filter(
              (item) =>
                !item.applicableWhen ||
                conditionPasses(formData, selectedItems, item.applicableWhen),
            )
          : [],
      );
    }, [formData, selectedItems]);

    // Count filled and unfilled fields
    const filledCount = allApplicableItems.filter(itemHasValue).length;
    const totalCount = allApplicableItems.length;
    const isFormPartiallyFilled = filledCount > 0 && filledCount < totalCount;

    const initialIsPartiallyFilled = useRef(isFormPartiallyFilled);

    const formItemRefs = useRef<(HTMLDivElement | null)[]>([]);

    const renderFormItem = (item: RiskAssessmentItemV1, itemIndex: number) => {
      const shouldRender =
        forTemplate ||
        !item.applicableWhen ||
        conditionPasses(formData, selectedItems, item.applicableWhen);

      const options = item.options ?? [];

      const isItemFilled = itemHasValue(item);

      return (
        <AnimatePresence initial={false} key={item.id}>
          {shouldRender && (
            <motion.div
              key={item.id}
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              transition={{ duration: 0.1 }}
              exit={{ height: 0, opacity: 0 }}
            >
              {/* Add yellow background if form was initially partially filled and this item is unfilled */}
              <div
                ref={(el) => {
                  formItemRefs.current[itemIndex] = el;
                }}
                className={cn(
                  "flex flex-col items-start gap-3 rounded-md px-4 py-2 transition-colors duration-300",
                  initialIsPartiallyFilled.current &&
                    !isItemFilled &&
                    "bg-yellow-50",
                )}
              >
                <div className="flex items-center gap-2">
                  <label
                    className={cn(
                      forTemplate &&
                        item.disabledInTemplate &&
                        "text-neutral-550",
                    )}
                  >
                    {forTemplate && item.disabledInTemplate ? (
                      <Tooltip
                        content="This question can only be answered by clients."
                        side="right"
                      >
                        <div className="flex flex-row items-center gap-2">
                          {item.title}
                          <Info className="h-4 w-4" />

                          {item.description && (
                            <Tooltip content={item.description}>
                              <Info className="h-4 w-4" />
                            </Tooltip>
                          )}
                        </div>
                      </Tooltip>
                    ) : (
                      <div className="text-heading-md flex flex-row items-center leading-[1.4]">
                        {item.title}

                        {item.description && (
                          <NgTooltip>
                            <NgTooltip.Trigger>
                              <Info className="ms-2 h-4 w-4" />
                            </NgTooltip.Trigger>
                            <NgTooltip.Content>
                              {item.description}
                            </NgTooltip.Content>
                          </NgTooltip>
                        )}

                        {canEdit &&
                          (selectedTemplateItems[item.id]
                            ? selectedItems[item.id] !==
                              selectedTemplateItems[item.id]
                            : !isEmpty(selectedItems[item.id])) && (
                            <NgTooltip>
                              <NgTooltip.Trigger>
                                <NgIconButton
                                  className="ms-2"
                                  variant="ghost"
                                  size="tiny"
                                  icon={<IconArrowBackUp size={16} />}
                                  onClick={() => {
                                    if (canEdit) {
                                      if (selectedTemplateItems[item.id]) {
                                        setFormValue(
                                          item.id,
                                          selectedTemplateItems[item.id],
                                        );
                                      } else {
                                        setFormValue(item.id, undefined);
                                      }
                                    }
                                  }}
                                />
                              </NgTooltip.Trigger>
                              <NgTooltip.Content side="right">
                                Reset this response
                              </NgTooltip.Content>
                            </NgTooltip>
                          )}
                      </div>
                    )}
                  </label>
                </div>
                {item.type === "YesNoItem" && (
                  <div className="flex flex-row gap-4">
                    {options.map((option) => (
                      <div key={option.id} className="flex items-center gap-2">
                        <Radio
                          color="accent"
                          id={`${item.id}__${option.id}`}
                          name={item.id}
                          value={option.id}
                          checked={item.selectedValueId === option.id}
                          onChange={(e) => {
                            if (!e.target.checked || !canEdit) return;
                            setFormValue(item.id, option.id);
                          }}
                          disabled={
                            !canEdit || (forTemplate && item.disabledInTemplate)
                          }
                        />
                        <label htmlFor={`${item.id}__${option.id}`}>
                          {option.label}
                        </label>
                      </div>
                    ))}
                  </div>
                )}
                {item.type === "MultiSelectItem" &&
                  options.map((option) => (
                    <div key={option.id} className="flex items-center gap-2">
                      <NgCheckbox
                        id={`${item.id}__${option.id}`}
                        name={item.id}
                        value={option.id}
                        checked={item.selectedValueIds?.includes(option.id)}
                        onCheckedChange={(checked) => {
                          if (!canEdit) return;
                          if (checked) {
                            setFormValue(item.id, [
                              ...new Set([
                                ...(item.selectedValueIds ?? []),
                                option.id,
                              ]),
                            ]);
                          } else {
                            setFormValue(
                              item.id,
                              (item.selectedValueIds ?? []).filter(
                                (id) => id !== option.id,
                              ),
                            );
                          }
                        }}
                        disabled={
                          !canEdit || (forTemplate && item.disabledInTemplate)
                        }
                      />
                      <label htmlFor={`${item.id}__${option.id}`}>
                        {option.label}
                      </label>
                    </div>
                  ))}
                {(item.type === "RadioItem" ||
                  item.type === "HighMediumLowItem") && (
                  <div className="flex flex-col gap-2">
                    {options.map((option) => (
                      <div key={option.id} className="flex items-center gap-2">
                        <Radio
                          color="accent"
                          id={`${item.id}__${option.id}`}
                          name={item.id}
                          value={option.id}
                          checked={item.selectedValueId === option.id}
                          onChange={(e) => {
                            if (!e.target.checked || !canEdit) return;
                            setFormValue(item.id, option.id);
                          }}
                          disabled={
                            !canEdit || (forTemplate && item.disabledInTemplate)
                          }
                        />
                        <label htmlFor={`${item.id}__${option.id}`}>
                          {option.label}
                        </label>
                      </div>
                    ))}
                  </div>
                )}
                {item.type === "CountriesItem" && (
                  <CountriesDropdown
                    disabled={
                      !canEdit || (forTemplate && item.disabledInTemplate)
                    }
                    initialCountries={
                      (item.selectedValueIds ?? []) as Iso3166Alpha2Code[]
                    }
                    onCountriesChanged={(countries) => {
                      if (canEdit) setFormValue(item.id, countries);
                    }}
                  />
                )}
                {item.type === "TextItem" && (
                  <NgTextArea
                    className="text-ng-lg m-1 !w-[99%]"
                    value={item.textValue ?? ""}
                    onChange={(e) => {
                      if (canEdit) setFormValue(item.id, e.target.value);
                    }}
                    disabled={
                      !canEdit || (forTemplate && item.disabledInTemplate)
                    }
                  />
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      );
    };

    const [activeCategory, setActiveCategory] = useState<string | null>(null);
    const categoryRefs = useRef<(HTMLDivElement | null)[]>([]);
    const categories = useMemo(
      () => riskAssessmentCategories(formData),
      [formData],
    );

    const SCROLL_CONTAINER_OFFSET = 300;

    // NOTE: Cursor code
    useImperativeHandle(
      ref,
      () => ({
        activeCategory,
        scrollToCategory: (categoryTitle: string) => {
          const container = scrollContainerRef?.current;
          if (!container) return;

          const idx = categories.findIndex((c) => c.title === categoryTitle);
          if (idx !== -1 && categoryRefs.current[idx]) {
            const element = categoryRefs.current[idx];
            if (element) {
              container.scrollTo({
                top: element.offsetTop - SCROLL_CONTAINER_OFFSET,
                behavior: "smooth",
              });
            }
          } else if (categoryTitle === "_top") {
            scrollContainerRef?.current?.scrollTo({
              top: 0,
              behavior: "smooth",
            });
          } else if (categoryTitle === "_riskLevel") {
            const riskLevelElement = container.querySelector(
              "#riskLevel",
            ) as HTMLElement;
            if (riskLevelElement) {
              container.scrollTo({
                top: riskLevelElement.offsetTop - SCROLL_CONTAINER_OFFSET,
                behavior: "smooth",
              });
            }
          }
        },
      }),
      [activeCategory, categories, scrollContainerRef],
    );

    // NOTE: Cursor code, doesn't quite work yet
    // TODO: Make this work
    useEffect(() => {
      if (!scrollContainerRef) return;
      const container = scrollContainerRef.current;
      if (!container) return;

      const handleScroll = () => {
        const offsets = categoryRefs.current.map((ref) => {
          if (!ref) return Infinity;
          const refTop = ref.clientTop - SCROLL_CONTAINER_OFFSET;
          const containerTop = container.clientTop - SCROLL_CONTAINER_OFFSET;
          // console.log(refTop, containerTop, Math.abs(refTop - containerTop));
          return Math.abs(refTop - containerTop);
        });
        const minOffset = Math.min(...offsets);
        const activeIndex = offsets.indexOf(minOffset);
        const newActive = categories[activeIndex]?.title ?? null;

        setActiveCategory((prev) => {
          if (prev !== newActive) {
            onActiveCategoryChange?.(newActive);
          }
          return newActive;
        });
      };

      container.addEventListener("scroll", handleScroll, { passive: true });
      handleScroll();
      return () => container.removeEventListener("scroll", handleScroll);
    }, [categories, onActiveCategoryChange, scrollContainerRef]);

    return (
      <>
        {isUpgradeConfirmationModalOpen && (
          <ConfirmationModal
            title="Upgrade questionnaire version?"
            color="brand"
            icon={<IconVersions size={20} />}
            confirmButtonText="Upgrade"
            cancelButtonText="Cancel"
            onClose={() => setIsUpgradeConfirmationModalOpen(false)}
            onConfirm={() => upgradeQuestionnaireVersion()}
          >
            <p>
              This will upgrade the vendor risk assessment questionnaire
              {vendor.name ? (
                <>
                  {" "}
                  for <b>{vendor.name}</b>
                </>
              ) : (
                ""
              )}{" "}
              to the latest version (
              {DEFAULT_VENDOR_RISK_ASSESSMENT.questionnaireVersion}).
            </p>
            <p className="mt-2">
              <b>Answers to some questions may be lost</b>, and you may need to
              answer some new questions.
            </p>
          </ConfirmationModal>
        )}

        <div className="flex flex-col gap-2">
          <h2 className="text-heading-md">Risk assessment questionnaire</h2>
          <p
            className={cn("text-ng-md text-neutral-550", forTemplate && "mb-4")}
          >
            The questions below are updated occasionally. When this happens, you
            can upgrade to the latest version here.
          </p>
        </div>
        <div className="mb-12 flex flex-row items-center justify-between">
          <div className="flex flex-row items-center gap-3">
            <IconClock size={22} className="" />
            <div className="flex flex-col gap-1">
              <p className="text-ng-base font-ng-semibold flex flex-row items-center gap-1">
                {questionnaireVersion}
                {questionnaireVersion === "Legacy"
                  ? " (prior to May 2025)"
                  : ""}
              </p>
              {canEdit && (
                <p className="text-ng-md text-neutral-550">
                  {canUpgradeQuestionnaireVersion
                    ? "An update to this questionnaire is available."
                    : "This is the latest questionnaire version."}
                </p>
              )}
            </div>
          </div>

          {canUpgradeQuestionnaireVersion ? (
            <NgButton
              color="accent"
              variant="solid"
              onClick={() => setIsUpgradeConfirmationModalOpen(true)}
            >
              <IconVersions size={20} />
              Upgrade questionnaire version
              <IconArrowRight size={20} />
            </NgButton>
          ) : (
            ""
          )}
        </div>

        {categories.map((category, i) => {
          const shouldDisplayCategory =
            forTemplate ||
            !category.applicableWhen ||
            conditionPasses(formData, selectedItems, category.applicableWhen);
          return (
            <AnimatePresence initial={false} key={i}>
              {shouldDisplayCategory && (
                <motion.div
                  ref={(el) => {
                    categoryRefs.current[i] = el;
                  }}
                  key={category.title}
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  transition={{ duration: 0.1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="flex flex-col gap-4 px-[2px]"
                >
                  <div className="mt-4 flex flex-col gap-2">
                    <h2 className="text-heading-xl flex flex-row items-center gap-2 font-semibold leading-6 text-gray-900">
                      <IconListDetails size={20} />
                      {category.title}
                    </h2>
                    {category.description && (
                      <p className="text-ng-base text-neutral-550">
                        {category.description}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-8 pb-4">
                    {category.items.map((item, idx) =>
                      renderFormItem(item, idx),
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          );
        })}
      </>
    );
  },
);
VendorRiskAssessment.displayName = "VendorRiskAssessment";
